import { useState, useMemo } from 'react'
import { FilterChip } from '@/components/ui/filter-chips'
import { formatted_user_role, formatted_user_status } from '@/entities/user'
import { PLANS_DATA } from '@/constants/hotmart-plans'

export interface AppliedFiltersState {
  search: string
  role: string
  planId: string
  status: string
  recordType: string
  [key: string]: string | number | boolean | undefined
}

export function useAppliedFilters() {
  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersState>({
    search: '',
    role: '',
    planId: '',
    status: '',
    recordType: ''
  })

  const filterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = []

    if (appliedFilters?.search) {
      chips.push({
        key: 'search',
        label: 'Busca',
        value: appliedFilters?.search,
        displayValue: appliedFilters?.search
      })
    }

    if (appliedFilters?.role) {
      chips.push({
        key: 'role',
        label: 'Função',
        value: appliedFilters?.role,
        displayValue: formatted_user_role[appliedFilters?.role as keyof typeof formatted_user_role] || appliedFilters?.role
      })
    }

    if (appliedFilters?.planId) {
      const plan = PLANS_DATA.find(p => p.id.toString() === appliedFilters?.planId)
      chips.push({
        key: 'planId',
        label: 'Plano',
        value: appliedFilters?.planId,
        displayValue: plan?.name || appliedFilters?.planId
      })
    }

    if (appliedFilters?.status) {
      chips.push({
        key: 'status',
        label: 'Status',
        value: appliedFilters?.status,
        displayValue: formatted_user_status[appliedFilters?.status as keyof typeof formatted_user_status] || appliedFilters?.status
      })
    }

    if (appliedFilters?.recordType) {
      const recordTypeLabels = {
        hotmart: 'Hotmart',
        local: 'Manual'
      }
      chips.push({
        key: 'recordType',
        label: 'Tipo de registro',
        value: appliedFilters?.recordType,
        displayValue: recordTypeLabels[appliedFilters?.recordType as keyof typeof recordTypeLabels] || appliedFilters?.recordType
      })
    }

    return chips
  }, [appliedFilters])

  const hasActiveFilters = useMemo(() => {
    return Object.values(appliedFilters || {})?.some(value => value !== '' && value !== undefined)
  }, [appliedFilters])

  const applyFilters = (filters: AppliedFiltersState) => {
    setAppliedFilters(filters)
  }

  const removeFilter = (key: string) => {
    setAppliedFilters(prev => ({
      ...prev,
      [key]: ''
    }))
  }

  const clearAllFilters = () => {
    setAppliedFilters({
      search: '',
      role: '',
      planId: '',
      status: '',
      recordType: ''
    })
  }

  return {
    appliedFilters,
    setAppliedFilters,
    filterChips,
    hasActiveFilters,
    applyFilters,
    removeFilter,
    clearAllFilters
  }
}
